import { useContext, useEffect, useState } from "react";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import { TextArea } from "../components/common/TextArea";
import { TextBox } from "../components/common/TextBox";
import PageContent from "../components/pageContent";
import PopupContent from "../components/popupContent";
import { apiGetAllBases } from "../services/BasesService";
import { apiCreateOrUpdatePackage, apiGetPackageDetail } from "../services/PackagesService";
import { apiGetAllProducts } from "../services/ProductsService";
import PopupOperationsBar from "./PopupOperationsBar";
import Select from 'react-select'
import { SelectBox } from "../components/common/SelectBox";
import { useDispatch } from "react-redux";
import { addNotification } from "../redux/actions";
import { NumericFormat } from 'react-number-format';
import { apiGetAllHardeners } from "../services/HardenerService";
import { NumericBox } from "../components/common/NumericBox";
import { ValidationProvider } from "../providers/ValidationContext";
import { model } from "../validation/models/package";
import { validateForm } from "../validation/formValidation";
import { FormatString } from "../helpers/stringHelpers";
import { useTranslation } from "react-i18next";
import TranslationContext from "../providers/TranslationContext";

// styles
require("./PopupGeneralStyles.css");

interface Props {
  id?: number;
  handleCancel: any;
  refreshData: any;
}

const PackagesPopup = (props: Props) => {
  const translationContext = useContext(TranslationContext) as any;
  const prefix = translationContext.prefix;
  const { t } = useTranslation(undefined, { keyPrefix: `${prefix}.Popup` });
  const cloneDeep = require('clone-deep');
  const [validationModel, setValidationModel] = useState(cloneDeep(model));

  const [loadingPackageDetails, setLoadingPackageDetails] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingBasis, setLoadingBasis] = useState(true);
  const [loadingHardeners, setLoadingHardeners] = useState(true);
  const [loading, setLoading] = useState(true);
  const data: any = {};
  const [formData, setFormData] = useState(data);
  const [saveError, setSaveError] = useState(false);
  const [products, setProducts] = useState([]);
  const [allBases, setAllBases] = useState([]);
  const [bases, setBases] = useState([]);
  const [hardeners, setHardeners] = useState([]);
  const [dtoHardeners, setDtoHardeners] = useState([]);

  const isEditMode = props.id ? true : false;

  const dispatch = useDispatch();

  const crypto = require("crypto");

  useEffect(() => {
    if (props.id) {
      let tempHardeners;
      apiGetPackageDetail(props.id)
        .then((responseData) => {
          var data = responseData.data;
          data.hardeners = data.hardeners.map((h: any) => {
            const id = crypto.randomBytes(16).toString("hex");
            return {
              value: h.id,
              uniqueId: `${h.id}_${id}`,
              label: `${h.name} (${h.size})`
            };
          });
          setFormData(data);
          setLoadingPackageDetails(false);
          setLoading(false);
        })
        .then(() => {
          apiGetAllHardeners().then((responseData) => {
            let arr = responseData.data.map((element) => {
              return {
                value: element.id,
                label: `${element.name} (${element.size})`,
              };
            });
            setHardeners(arr);
            setDtoHardeners(responseData.data);
            setLoadingHardeners(false);
            setLoading(false);
          });
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('LoadingDetailsError'), `${props.id}`), type: 'error' }));
          props.handleCancel();
        });
      apiGetAllProducts(false)
        .then((responseData) => {
          let arr = responseData.data.map((element) => {
            return {
              value: element.id,
              label: element.name
            };
          });
          setProducts(arr);
          setLoadingProducts(false);
          setLoading(false);
        });
      apiGetAllBases()
        .then((responseData) => {
          let arr = responseData.data.map((element) => {
            return {
              value: element.id,
              label: element.name,
              productId: element.productId
            };
          });
          setAllBases(arr);
          setLoadingBasis(false);
          setLoading(false);
        });

    } else {
      apiGetAllProducts(false)
        .then((responseData) => {
          let arr = responseData.data.map((element) => {
            return {
              value: element.id,
              label: element.name
            };
          });
          setProducts(arr);
          setLoadingProducts(false);
          setLoading(false);
        });
      apiGetAllBases()
        .then((responseData) => {
          let arr = responseData.data.map((element) => {
            return {
              value: element.id,
              label: element.name,
              productId: element.productId
            };
          });
          setAllBases(arr);
          setLoadingBasis(false);
          setLoading(false);
        });
      apiGetAllHardeners().then((responseData) => {

        let arr = responseData.data.map((element) => {
          return {
            value: `${element.id}`,
            label: `${element.name} (${element.size})`
          };
        });

        setHardeners(arr);
        setDtoHardeners(responseData.data);
        setLoadingHardeners(false);
        setLoading(false);
      });
      setLoading(false);
    }
  }, []);

  const onAddingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      if (formData.hardeners?.length > 0) {
        formData.hardeners = formData.hardeners.map((h: any) => {
          return dtoHardeners.find((o: any) => { return o.id == h.value });
        });
      }
      apiCreateOrUpdatePackage(formData)
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('CreateSuccess'), formData.code), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('CreateFailure'), formData.code), type: 'error' }));
          props.handleCancel();

          setSaveError(true);
        });
    }
  };

  const onEditingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      if (formData.hardeners?.length > 0) {
        formData.hardeners = formData.hardeners.map((h: any) => {
          return dtoHardeners.find((o: any) => { return o.id == h.value });
        });
      }
      apiCreateOrUpdatePackage(formData)
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('EditSuccess'), formData.code), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('EditFailure'), formData.code), type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onCancelClick = () => {
    props.handleCancel();
  };

  useEffect(() => {
    if (formData.productId && !loadingBasis) {
      let filteredBases = allBases.filter((f: any) => { return f.productId === formData.productId });
      setBases(filteredBases);

      let selectedBase = filteredBases.find((b: any) => { return b.value === formData.basisId });
      if (!selectedBase) {
        formData.basisId = undefined;
        setFormData({ ...formData });
      }
    }
  }, [formData.productId, loadingBasis]);

  return (
    <PopupContent header={props.id ? t('EditLabel') : t('CreateLabel')}>
      <ValidationProvider value={validationModel}>
        <SelectBox
          dataField="productId"
          value={formData.productId}
          formData={formData}
          label={t('Labels.product')}
          options={products}
          placeholder={t('Labels.productPlaceholder')}
          setStateMethod={setFormData}
          isDisabled={isEditMode}
        />
        <SelectBox
          dataField="basisId"
          value={formData.basisId}
          formData={formData}
          label={t('Labels.base')}
          options={bases}
          placeholder={t('Labels.basePlaceholder')}
          setStateMethod={setFormData}
          isDisabled={!formData.productId}
        />
        <SelectBox
          dataField="hardeners"
          value={formData.hardeners}
          formData={formData}
          label={t('Labels.hardeners')}
          options={hardeners}
          placeholder={t('Labels.hardenersPlaceholder')}
          setStateMethod={setFormData}
          isDisabled={false}
          isMulti={true}
          hideSelectedOptions={false}
          className="select-box-multi"
        />
        <TextBox
          dataField="code"
          defaultValue={formData.code}
          formData={formData}
          setStateMethod={setFormData}
          id="code"
          label={t('Labels.code')}
          placeholder={t('Labels.codePlaceholder')}
          disabled={false}
        />
        <NumericBox
          dataField="amount"
          defaultValue={formData.amount}
          formData={formData}
          setStateMethod={setFormData}
          id="amount"
          label={t('Labels.amount')}
          decimalPlaces={3}
        />
        <NumericBox
          dataField="price"
          defaultValue={formData.price}
          formData={formData}
          setStateMethod={setFormData}
          id="price"
          label={t('Labels.price')}
          decimalPlaces={2}
        />
        {
        /*
        <NumericBox
          dataField="margin"
          defaultValue={formData.margin}
          formData={formData}
          setStateMethod={setFormData}
          id="margin"
          label={t('Labels.margin')}
          decimalPlaces={2}
        />
        */
        }
        <TextArea
          id="noteInternal"
          label={t('Labels.note')}
          placeholder={t('Labels.notePlaceholder')}
          rows={4}
          dataField="noteInternal"
          value={formData.noteInternal}
          formData={formData}
          setStateMethod={setFormData}
        />

        <PopupOperationsBar
          handleAdding={onAddingClick}
          handleCancellation={onCancelClick}
          handleEditing={onEditingClick}
          id={props.id}
        />
      </ValidationProvider>
    </PopupContent>
  );
};

export default PackagesPopup;
